import React, { useCallback } from "react";
import styles from "./users-deactivation-settings-page.module.scss";
import { Button } from "@whatslly/ui-components/v2/Button";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import classnames from "classnames";
import { useDependencyInjector } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { CrmComponentsErrorBox } from "@whatslly/ui-components/v2/CrmComponentsErrorBox";
import { LOADER_IMG } from "@whatslly/commons/constants";
import type { IntervalOptionType } from "../../types/IntervalOptionType";
import { AutoSyncActivitiesSettingsStore } from "../../stores/AutoSyncActivitiesSettingsStore";
import { INTERVAL_OPTIONS } from "../../stores/IntervalOptions";

export const AutoSyncActivitiesSettingsPage: React.FC<{ hideSaveButton?: boolean }> = observer(({ hideSaveButton }) => {
  const autoSyncActivitiesSettingsStore = useDependencyInjector(AutoSyncActivitiesSettingsStore);

  const { t } = useTranslation();

  const runNow = useCallback(async () => {
    modalService.openModal(Toast, {
      text: t("autoSyncActivitiesPage.modal_start_now"),
    });
    await autoSyncActivitiesSettingsStore.runNow();
    modalService.openModal(Toast, {
      text: t("autoSyncActivitiesPage.modal_finish_now"),
    });
  }, [autoSyncActivitiesSettingsStore, t]);

  const saveSettings = useCallback(async () => {
    modalService.openModal(Toast, {
      text: t("autoSyncActivitiesPage.modal_saved_interval"),
    });
    await autoSyncActivitiesSettingsStore.saveSettings();
  }, [autoSyncActivitiesSettingsStore, t]);

  return (
    <div className={styles.page}>
      <div className={styles.header} data-testid={"auto-sync-activities"}>
        {t("autoSyncActivitiesPage.header")}
      </div>
      {autoSyncActivitiesSettingsStore.init.case({
        pending: () => (
          <div className={styles.loading}>
            <img alt="tuvis loader" src={LOADER_IMG} />
            <div>{t("autoSyncActivitiesPage.loading")}</div>
          </div>
        ),
        fulfilled: ({ showRunNowJob }) => (
          <>
            <div className={styles.desc}>{t("autoSyncActivitiesPage.desc")}</div>
            <div className={styles.select}>
              <Select<IntervalOptionType>
                disabled={autoSyncActivitiesSettingsStore.isSaving}
                options={INTERVAL_OPTIONS}
                value={autoSyncActivitiesSettingsStore.selectedOption}
                onChange={(item: IntervalOptionType) => autoSyncActivitiesSettingsStore.setSelectedOption(item)}
              />
            </div>
            <div className={styles.runNowSection}>
              <div>{autoSyncActivitiesSettingsStore.selectedOption?.description}</div>
              {showRunNowJob && (
                <div onClick={runNow} className={classnames(styles.runNowButton, { [styles.disabled]: autoSyncActivitiesSettingsStore.isRunning })}>
                  {t("autoSyncActivitiesPage.runNow")}
                </div>
              )}
            </div>
            {!hideSaveButton && (
              <Button
                isSubmitting={autoSyncActivitiesSettingsStore.isSaving}
                disabled={autoSyncActivitiesSettingsStore.isSaving}
                testId={`save-settings`}
                paddingInline="small"
                fontSize={`small`}
                onClick={saveSettings}
              >
                {t("autoSyncActivitiesPage.saveSettings")}
              </Button>
            )}
            {(autoSyncActivitiesSettingsStore.previousRun || autoSyncActivitiesSettingsStore.nextRun) && (
              <div className={styles.scheduleSection}>
                {autoSyncActivitiesSettingsStore.previousRun && (
                  <div data-testid="previous-run">
                    {t("autoSyncActivitiesPage.previousRun")}: {autoSyncActivitiesSettingsStore.previousRun}
                  </div>
                )}
                {autoSyncActivitiesSettingsStore.nextRun && (
                  <div data-testid="next-run">
                    {t("autoSyncActivitiesPage.nextRun")}: {autoSyncActivitiesSettingsStore.nextRun}
                  </div>
                )}
              </div>
            )}
          </>
        ),
        rejected: (error: Error) => <CrmComponentsErrorBox error={error.message} />,
      })}
    </div>
  );
});
